import React, { useState, useEffect } from "react";
import { ClipboardList } from "lucide-react";
import Modal from "../../common/Modal";
import Toast, { ToastMessage } from "../../common/Toast";
import sopInstanceService, { ROLE_LABEL } from "../../../services/sopInstanceService";
import { governanceProcessStepsService, type ProcessStep } from "../../../services/governanceService";

interface Props {
  processId: number;
  processName: string;
  onClose: () => void;
}

/**
 * Read-only "dry run" of an SOP template's steps for the staff persona. Lists
 * every step in order and highlights the ones the logged-in person is concerned
 * with — a step is mine when any of its authorities is one I hold (a person may
 * hold several authorities, so several steps can be mine). Nothing is initiated
 * here; it's a preview of "what would be my steps if I ran this".
 */
const SopStepsPreviewModal: React.FC<Props> = ({ processId, processName, onClose }) => {
  const [steps, setSteps] = useState<ProcessStep[]>([]);
  const [myAuthorityIds, setMyAuthorityIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, mine] = await Promise.all([
          governanceProcessStepsService.getByProcess(processId),
          sopInstanceService.getMyAuthorities().catch(() => [] as number[]),
        ]);
        setSteps(s);
        setMyAuthorityIds(mine);
      } catch {
        setToast({ type: "error", message: "Failed to load steps." });
      } finally {
        setLoading(false);
      }
    })();
  }, [processId]);

  const mineCount = steps.filter((st) =>
    (st.authorities ?? []).some((a) => myAuthorityIds.includes(a.authority_id)),
  ).length;

  return (
    <>
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
      <Modal isOpen onClose={onClose} size="lg">
        <Modal.Header onClose={onClose}>
          
              SOP Preview -
{processName}
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="flex flex-col gap-2" aria-busy="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : steps.length === 0 ? (
            <p className="text-sm text-gray-400 italic text-center py-8" data-testid="EMPTY_sop_preview_steps">
              No steps defined for this SOP.
            </p>
          ) : (
            <div className="space-y-3" data-testid="PANEL_sop_preview_steps">
              <p className="text-xs text-gray-500">
                Dry run — all steps shown. <span className="text-blue-600 font-semibold">{mineCount}</span>{" "}
                {mineCount === 1 ? "step is" : "steps are"} yours (highlighted).
              </p>
              {steps.map((step) => {
                const auths = step.authorities ?? [];
                const isMine = auths.some((a) => myAuthorityIds.includes(a.authority_id));
                return (
                  <div
                    key={step.id}
                    data-testid={`ROW_sop_preview_step_${step.id}`}
                    className={`border rounded-xl px-4 py-3 transition-all ${
                      isMine ? "border-blue-300 bg-blue-50/40" : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {step.step_number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {step.step_name}
                          {isMine && <span className="ml-2 text-blue-500 font-semibold text-xs">· You</span>}
                        </p>
                      </div>
                    </div>
                    {auths.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2 pl-9">
                        {auths.map((a) => {
                          const mine = myAuthorityIds.includes(a.authority_id);
                          return (
                            <span
                              key={`${a.authority_id}-${a.role}`}
                              className={`text-[11px] px-2 py-0.5 rounded-full border ${
                                mine
                                  ? "bg-blue-100 text-blue-700 border-blue-200 font-semibold"
                                  : "bg-gray-50 text-gray-500 border-gray-200"
                              }`}
                            >
                              {a.authority_name} — {ROLE_LABEL[a.role] ?? a.role}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SopStepsPreviewModal;
